import { Injectable, signal, computed, OnDestroy } from '@angular/core';

/**
 * PwaInstallService
 *
 * Manages the PWA installation lifecycle:
 * - Captures the `beforeinstallprompt` event to allow in-app install prompts
 * - Tracks whether the app is already installed (running in standalone mode)
 * - Tracks whether the prompt was dismissed by the user
 *
 * Usage:
 *   inject(PwaInstallService).install() → shows the native install dialog
 */
@Injectable({ providedIn: 'root' })
export class PwaInstallService implements OnDestroy {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  /** `true` when the browser has a pending install prompt available */
  readonly canInstall = signal(false);

  /** `true` after the user accepted or dismissed the install prompt */
  readonly promptHandled = signal(false);

  /** `true` if the app is currently running as an installed PWA */
  readonly isStandalone = computed(() => this.checkStandalone());

  /**
   * `true` when the floating banner should be shown.
   * Requires the browser to have fired `beforeinstallprompt` AND the user not yet handled it.
   */
  readonly showBanner = computed(() => this.canInstall() && !this.isStandalone() && !this.promptHandled());

  /**
   * `true` whenever the install UI (button/chip) should be visible.
   * Does NOT require `beforeinstallprompt` — it simply checks the app is not
   * already running as an installed PWA. This ensures the button is always
   * available even after the user uninstalled the app and the browser's
   * cooldown has not yet expired.
   */
  readonly canShowInstallUI = computed(() => !this.isStandalone());

  /**
   * `true` when the browser has a deferred native install prompt ready.
   * When `false`, the install button should show manual installation instructions.
   */
  readonly hasNativePrompt = computed(() => this.canInstall());

  private readonly onBeforeInstallPrompt = (event: Event): void => {
    // Prevent the default mini-infobar on mobile Chrome
    event.preventDefault();
    this.deferredPrompt = event as BeforeInstallPromptEvent;
    this.canInstall.set(true);
  };

  private readonly onAppInstalled = (): void => {
    this.deferredPrompt = null;
    this.canInstall.set(false);
    this.promptHandled.set(true);
  };

  constructor() {
    window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    window.addEventListener('appinstalled', this.onAppInstalled);
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', this.onAppInstalled);
  }

  /**
   * Shows the browser's native PWA install dialog.
   * Returns `true` if the user accepted, `false` if dismissed.
   */
  async install(): Promise<boolean> {
    if (!this.deferredPrompt) return false;

    await this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;

    this.deferredPrompt = null;
    this.canInstall.set(false);
    this.promptHandled.set(true);

    return outcome === 'accepted';
  }

  /** Permanently hides the install banner for this session */
  dismissBanner(): void {
    this.promptHandled.set(true);
  }

  private checkStandalone(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in window.navigator &&
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true)
    );
  }
}

// Extend the Window type to include the deferred prompt interface
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}
